import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  Link,
} from '@mui/material';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { getFormattedRuPhone, getFullName } from 'core/utils/commonUtils';
import useUsers from 'hooks/swr/useUsers';
import useResponsive from 'hooks/useResponsive';
import { differenceWith, isEmpty, isNil, remove } from 'lodash';
import { useEffect, useState } from 'react';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import { object, string } from 'yup';
import type {
  GridColDef,
  GridRowSelectionModel,
  GridCallbackDetails,
} from '@mui/x-data-grid';
import type { OrderActionEnum } from 'models/enums/orderActionEnum';
import type { User } from 'models/user/user';
import type { FC } from 'react';
import type { SubmitHandler } from 'react-hook-form-mui';

interface UsersFilterData {
  search: string;
}

const UserSelectDialog: FC<UserSelectProps> = ({
  open,
  onSave,
  onCancel,
  actionAccesses,
  description,
}) => {
  const fullScreen = useResponsive('down', 'sm');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [searchText, setSearchText] = useState('');
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<User[]>([]);

  const schema = object<UsersFilterData>({
    search: string().max(300),
  }).required();

  const formContext = useForm<UsersFilterData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (open) {
      formContext.setValue('search', '');
      setRowSelectionModel([]);
      setSelectedRowsData([]);
      setPaginationModel({
        page: 0,
        pageSize: 25,
      });
    }
  }, [open, formContext]);

  const { data, isLoading } = useUsers({
    pageIndex: paginationModel.page,
    pageSize: paginationModel.pageSize,
    filterText: searchText,
    actionAccesses,
  });

  const columns: Array<GridColDef<User>> = [
    {
      field: 'name',
      headerName: 'Имя',
      sortable: false,
      flex: 0.6,
      display: 'flex',
      valueGetter: (_, row) => getFullName(row),
    },
    {
      field: 'phone',
      headerName: 'Телефон',
      sortable: false,
      maxWidth: 200,
      flex: 0.4,
      display: 'flex',
      renderCell: (params) => (
        <Link href={`tel:+${params.value as string}`}>
          {getFormattedRuPhone(params.value)}
        </Link>
      ),
    },
  ];

  const filter: SubmitHandler<UsersFilterData> = (data) => {
    setSearchText(data.search);
  };

  const saveClickHandler: VoidFunction = () => {
    onSave(selectedRowsData);
  };

  const selectionModelChangeHandler = (
    newRowSelectionModel: GridRowSelectionModel,
    _: GridCallbackDetails
  ): void => {
    const deleted = differenceWith(
      selectedRowsData,
      newRowSelectionModel,
      (a, b) => a.id === +b
    ).map((p) => p.id);

    const newData = differenceWith(
      newRowSelectionModel,
      selectedRowsData,
      (a, b) => +a === b.id
    ).map((d) => +d);

    remove(selectedRowsData, (p) => deleted.includes(p.id));

    newData.forEach((n) => {
      const user = data?.items?.find((d) => d.id === n);
      if (!isNil(user)) {
        selectedRowsData.push(user);
      }
    });

    setSelectedRowsData(selectedRowsData);

    setRowSelectionModel(newRowSelectionModel);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={'md'}
      fullWidth
      fullScreen={fullScreen}>
      <DialogTitle>Сотрудники</DialogTitle>
      <DialogContent>
        {!isEmpty(description) && (
          <DialogContentText gutterBottom>{description}</DialogContentText>
        )}

        <FormContainer formContext={formContext} onSuccess={filter}>
          <TextFieldElement
            sx={{ marginBottom: 2 }}
            size='small'
            fullWidth
            name='search'
            placeholder='Поиск'
            type='search'
          />
        </FormContainer>
        <DataGrid
          sx={{
            [`& .${gridClasses.cell}:not(.${gridClasses.cellCheckbox}):not([data-field="actions"])`]:
              {
                py: 2,
              },
            [`& .${gridClasses.cell}:focus`]: {
              outline: 'none',
            },
          }}
          rows={data?.items ?? []}
          rowCount={data?.totalCount ?? 0}
          loading={isLoading}
          disableColumnMenu={true}
          getRowHeight={() => 'auto'}
          autoHeight
          checkboxSelection
          keepNonExistentRowsSelected
          paginationModel={paginationModel}
          paginationMode='server'
          onPaginationModelChange={setPaginationModel}
          columns={columns}
          onRowSelectionModelChange={selectionModelChangeHandler}
          rowSelectionModel={rowSelectionModel}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={saveClickHandler}>Готово</Button>
        <Button onClick={onCancel}>Отмена</Button>
      </DialogActions>
    </Dialog>
  );
};

// TODO: Сделать возможность выбора одного или нескольких пользователей
export interface UserSelectProps {
  actionAccesses?: OrderActionEnum[];
  open: boolean;
  description?: string;
  onSave: (selectedUserIds: User[]) => void;
  onCancel: () => void;
}

export default UserSelectDialog;
